import { useEffect, useState } from 'react'
import { getFaxFileSignedUrl } from '@services/api/fax/document.api'

const hasUsableSignature = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    if (!parsed.searchParams.has('sig')) return false

    const expiresAt = parsed.searchParams.get('se')
    if (!expiresAt) return false

    const expiresAtMs = Date.parse(expiresAt)
    return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now() + 60_000
  } catch {
    return false
  }
}

export const toStableBlobUrl = (url: string): string => {
  const trimmed = url.trim()
  try {
    const parsed = new URL(trimmed)
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return trimmed.replace(/[?#].*$/, '')
  }
}

export async function resolveSignedBlobUrl(
  url: string | null | undefined,
  fallback: string,
): Promise<string> {
  const trimmed = url?.trim() ?? ''
  if (!trimmed) return fallback
  if (hasUsableSignature(trimmed)) return trimmed

  try {
    const response = await getFaxFileSignedUrl(toStableBlobUrl(trimmed))
    return response.data?.url || fallback
  } catch {
    return fallback
  }
}

/**
 * Azure Blob URLs are private; the browser can only load them through a
 * presigned (SAS) URL. Returns `fallback` until the signed URL resolves,
 * or when there is no URL / signing fails.
 */
export function useSignedBlobUrl(
  url: string | null | undefined,
  fallback: string,
): string {
  const trimmed = url?.trim() ?? ''
  const [signedUrl, setSignedUrl] = useState<string>(() =>
    trimmed && hasUsableSignature(trimmed) ? trimmed : fallback,
  )

  useEffect(() => {
    if (!trimmed) {
      setSignedUrl(fallback)
      return
    }
    if (hasUsableSignature(trimmed)) {
      setSignedUrl(trimmed)
      return
    }

    let cancelled = false
    setSignedUrl(fallback)
    resolveSignedBlobUrl(trimmed, fallback)
      .then((resolvedUrl) => {
        if (!cancelled) setSignedUrl(resolvedUrl)
      })
      .catch(() => {
        if (!cancelled) setSignedUrl(fallback)
      })

    return () => {
      cancelled = true
    }
  }, [trimmed, fallback])

  return signedUrl
}
